import type { DotLottieWorker as DotLottieWorkerType } from "@lottiefiles/dotlottie-web"

// Preload all lottie animation asset urls
const animations = import.meta.glob(
  [
    "images/animations/home/automate_everyday/*.lottie",
    "images/home/prosumer/smarthome*.lottie",
    "images/home/prosumer/productivity*.lottie",
  ],
  {
    query: "?url",
    eager: true,
  }
)

export class HomepageModuleAnimation {
  dotLottie: DotLottieWorkerType | null = null
  observer: IntersectionObserver | null = null
  container: HTMLElement
  loaded: boolean = false
  isMobile: boolean = false

  constructor(container: HTMLElement) {
    this.container = container
    this.isMobile = window.innerWidth < 724
    this.setupIntersectionObserver()
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: "100px",
      threshold: 0.1,
    }

    this.observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.loaded) {
          this.loadAndPlayAnimation()
          this.loaded = true
          this.observer?.disconnect()
        }
      })
    }, options)

    this.observer.observe(this.container)
  }

  async loadAndPlayAnimation() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return
    }

    const animationPath = this.isMobile
      ? this.container.dataset.animationMobile
      : this.container.dataset.animationDesktop

    try {
      const animationUrl = (animations[animationPath as string] as { default: string })?.default

      if (!animationUrl) {
        console.error("Animation not found:", animationPath)
        return
      }

      // the player library will be loaded by the header animation, included here in case this module is used standalone
      const { DotLottieWorker } = await import("@lottiefiles/dotlottie-web")

      const canvas = document.createElement("canvas")
      canvas.style.width = "100%"
      canvas.style.height = "100%"
      this.container.appendChild(canvas)

      this.dotLottie = new DotLottieWorker({
        canvas,
        src: animationUrl,
        workerId: "dotlottie-shared",
        loop: true,
        autoplay: true,
      })
    } catch (error) {
      console.error("Failed to load dotLottie animation:", error)
    }
  }
}

// Initialize all homepage module animations on page load
window.addEventListener("load", () => {
  document.querySelectorAll(".homepage-module__animation-container").forEach(container => {
    new HomepageModuleAnimation(container as HTMLElement)
  })
})
